import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Strike } from '@tiptap/extension-strike';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { FontSize } from '../components/FontSize';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { ResizableImage } from '../components/ResizableImage';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough as StrikethroughIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ImagePlus,
  AlignHorizontalDistributeStart,
  AlignHorizontalDistributeCenter,
  AlignHorizontalDistributeEnd,
  Undo as UndoIcon,
  Redo as RedoIcon,
  RotateCcw,
  ArrowLeft,
  Settings as SettingsIcon,
  Trash2,
  Plus,
  X,
  Pencil,
  Pin,
  BarChart3,
  MessageSquare,
  Image as ImageIcon,
  GripVertical,
  Download,
  BookOpen,
  MoreHorizontal,
  Focus,
  ChevronDown,
  ChevronUp,
  Bot,
} from 'lucide-react';
import EditorToolbar from '../components/EditorToolbar';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchMemoir,
  updateMemoir,
  selectCurrentMemoir,
  selectMemoirLoading,
  selectMemoirError,
  fetchTodos,
  createTodo,
  updateTodo,
  deleteTodo,
  selectMemoirTodos
} from '../store/slices/memoirSlice';
import {
  fetchChapters,
  createChapter,
  updateChapter,
  deleteChapter,
  saveChapterContent,
  reorderChapters,
  fetchNotes,
  createNote,
  updateNote,
  deleteNote,
  selectChapters,
  selectCurrentChapter,
  selectChapterLoading,
  selectSaveStatus,
  selectLastSaved,
  selectChapterNotes,
  setCurrentChapter,
  setSaveStatus
} from '../store/slices/chapterSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import UsageLimits from '../components/UsageLimits';
import ChapterAnalysis from '../components/ChapterAnalysis';
import { ChatInterface } from '../components/ChatInterface';
import ImageGeneration from '../components/ImageGeneration';
import AIImprovementModal from '../components/AIImprovementModal';
import ExportModal from '../components/ExportModal';
import { apiClient } from '../services/api';
import { UserProfile, UpgradePromptData, SubscriptionTier } from '../types/api';
import UpgradePrompt from '../components/UpgradePrompt';
import './Editor.css';

// Note: Using types from Redux store instead of local interfaces

const Editor: React.FC = () => {
  // URL params
  const { memoirId, chapterId } = useParams<{ memoirId: string; chapterId?: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux state
  const memoir = useAppSelector(selectCurrentMemoir);
  const chapters = useAppSelector(selectChapters);
  const currentChapter = useAppSelector(selectCurrentChapter);
  const memoirLoading = useAppSelector(selectMemoirLoading);
  const chapterLoading = useAppSelector(selectChapterLoading);
  const saveStatus = useAppSelector(selectSaveStatus);
  const lastSaved = useAppSelector(selectLastSaved);
  const memoirError = useAppSelector(selectMemoirError);
  const notes = useAppSelector(selectChapterNotes);
  const todos = useAppSelector(selectMemoirTodos);

  // Local state
  const [activeTab, setActiveTab] = useState<'notes' | 'todos' | 'ai'>('ai');
  const [aiSubTab, setAiSubTab] = useState<'analysis' | 'chat' | 'image'>('chat');
  const [showNewChapterModal, setShowNewChapterModal] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterGroup, setNewChapterGroup] = useState('');
  const [draggedChapter, setDraggedChapter] = useState<string | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editableTitle, setEditableTitle] = useState('');
  const [showToolbarMore, setShowToolbarMore] = useState(false);
  const [titleSaveTimeout, setTitleSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editableGroupName, setEditableGroupName] = useState('');
  const [groupSaveTimeout, setGroupSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isEditingChapterTitle, setIsEditingChapterTitle] = useState(false);
  const [editableChapterTitle, setEditableChapterTitle] = useState('');
  const [chapterTitleSaveTimeout, setChapterTitleSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);
  const [showDeleteChapterModal, setShowDeleteChapterModal] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState<any>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Focus mode state
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [focusBtnHighlight, setFocusBtnHighlight] = useState(false);
  const focusHighlightTimer = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  // Highlight focus button for 3 seconds when focus mode changes
  useEffect(() => {
    // Skip initial render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Clear any existing timer
    if (focusHighlightTimer.current) {
      clearTimeout(focusHighlightTimer.current);
    }

    setFocusBtnHighlight(true);
    focusHighlightTimer.current = setTimeout(() => {
      setFocusBtnHighlight(false);
    }, 3000);

    return () => {
      if (focusHighlightTimer.current) {
        clearTimeout(focusHighlightTimer.current);
      }
    };
  }, [isFocusMode]);

  // Auto-enable focus mode after delay on narrow browsers, disable on wide
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // Enable focus mode after delay if starting narrow
    // Only auto-enable focus mode on tablet (768-1500px), not mobile
    if (window.innerWidth >= 768 && window.innerWidth < 1500) {
      timer = setTimeout(() => {
        setIsFocusMode(true);
      }, 2000);
    }

    // Listen for resize to toggle focus mode based on width (tablet only)
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1500 || width < 768) {
        // Wide desktop or mobile - disable focus mode
        setIsFocusMode(false);
      } else {
        // Tablet range (768-1500px) - enable focus mode
        setIsFocusMode(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Mobile chapters expanded state
  const [mobileChaptersExpanded, setMobileChaptersExpanded] = useState(false);

  // Mobile bottom panel mode: 'chapters' or 'ai'
  const [mobileBottomPanel, setMobileBottomPanel] = useState<'chapters' | 'ai'>('chapters');
  const [mobileAiExpanded, setMobileAiExpanded] = useState(false);

  // Notes state
  const [showNewNoteModal, setShowNewNoteModal] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  // Todos state
  const [showNewTodoModal, setShowNewTodoModal] = useState(false);
  const [newTodoDescription, setNewTodoDescription] = useState('');
  const [newTodoPriority, setNewTodoPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [newTodoDueDate, setNewTodoDueDate] = useState('');

  // Auto-save debouncing
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // AI Paragraph Improvement state
  const [showAIImprovementModal, setShowAIImprovementModal] = useState(false);
  const [isImprovingParagraph, setIsImprovingParagraph] = useState(false);
  const [originalParagraphText, setOriginalParagraphText] = useState('');
  const [improvedParagraphText, setImprovedParagraphText] = useState<string | null>(null);
  const [improvementError, setImprovementError] = useState<string | null>(null);
  const [improvementUsageInfo, setImprovementUsageInfo] = useState<any>(null);
  const [lastParagraphImprovement, setLastParagraphImprovement] = useState<{
    originalText: string;
    improvedText: string;
    position: { from: number; to: number };
  } | null>(null);

  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Strike,
      TextStyle,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSize.configure({
        types: ['textStyle'],
      }),
      Color.configure({
        types: ['textStyle'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      ResizableImage.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
    ],
    content: '<p>Please select a chapter</p>',
    editable: false, // Start as read-only to prevent accidental edits
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[500px] p-4',
      },
      // Strip formatting from pasted content - keep only plain text with paragraphs
      transformPastedHTML(html: string) {
        // Create a temporary element to parse the HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;

        // Extract text content, preserving paragraph breaks
        const paragraphs: string[] = [];
        const walk = (node: Node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text) {
              paragraphs.push(text);
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            const tag = el.tagName.toLowerCase();

            // Block elements create paragraph breaks
            if (['p', 'div', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tag)) {
              if (el.textContent?.trim()) {
                paragraphs.push(el.textContent.trim());
              }
            } else {
              // Inline elements - recurse into children
              el.childNodes.forEach(child => walk(child));
            }
          }
        };

        temp.childNodes.forEach(child => walk(child));

        // Return clean paragraphs without any formatting
        return paragraphs.map(p => `<p>${p}</p>`).join('');
      },
      handleDOMEvents: {
        contextmenu: (view, event) => {
          // Don't show context menu if read-only
          if (userProfile?.is_read_only) {
            return false;
          }

          event.preventDefault();

          // Get the position from the click
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
          if (!pos) return false;

          // Find the paragraph node at this position
          const $pos = view.state.doc.resolve(pos.pos);
          let paragraphNode = null;
          let paragraphPos = null;

          // Debug logging
          console.log('Right-click debug:', {
            clickPos: pos.pos,
            depth: $pos.depth,
            nodeAtPos: $pos.node($pos.depth)?.type.name,
            parentNode: $pos.node(Math.max(0, $pos.depth - 1))?.type.name
          });

          // Traverse up to find a paragraph node (including depth 0)
          for (let d = $pos.depth; d >= 0; d--) {
            const node = $pos.node(d);
            console.log(`Checking depth ${d}: node type = ${node.type.name}`);
            if (node.type.name === 'paragraph') {
              paragraphNode = node;
              paragraphPos = $pos.before(d);
              console.log('Found paragraph at depth:', d, 'text:', node.textContent);
              break;
            }
          }

          if (!paragraphNode || paragraphPos === null) {
            console.log('No paragraph found or position is null');
            return false;
          }

          // Extract the paragraph text
          const paragraphText = paragraphNode.textContent;

          // Validate word count (5-1000 words)
          const wordCount = paragraphText.split(/\s+/).filter(w => w.length > 0).length;

          if (wordCount < 5) {
            alert('Paragraph is too short to improve (minimum 5 words required).');
            return true;
          }

          if (wordCount > 1000) {
            alert(`Paragraph exceeds 1000 word limit. Your paragraph has ${wordCount} words. Please select a shorter paragraph.`);
            return true;
          }

          // Show the improvement modal with the paragraph text
          handleImproveParagraph(paragraphText, paragraphPos, paragraphPos + paragraphNode.nodeSize);

          return true; // Prevent default context menu
        },
      },
    },
    onUpdate: ({ editor }) => {
      // Don't save if user is in read-only mode
      if (userProfile?.is_read_only) {
        return;
      }

      // Auto-save with debouncing
      // Only auto-save if we have a chapter, content is not loading, and editor has actual content
      if (currentChapter && !contentLoading && !memoirLoading) {
        const content = editor.getJSON();
        const plainText = editor.getText();

        // Don't save if the content is just the loading placeholder or empty
        if (content && content.content && content.content.length > 0 &&
          !plainText.includes('Loading') && !plainText.includes('Please select a chapter') && plainText.trim().length > 0) {

          // Clear existing timeout but don't set saving status immediately
          if (autoSaveTimeout) {
            clearTimeout(autoSaveTimeout);
          }

          // Capture the current chapter ID at the time of typing
          const chapterIdAtTimeOfEdit = currentChapter.chapter_id;

          const timeout = setTimeout(() => {
            // Only set saving status right before actually saving
            dispatch(setSaveStatus('saving'));
            handleAutoSave(content, chapterIdAtTimeOfEdit);
          }, 1000); // More aggressive 1 second save

          setAutoSaveTimeout(timeout);
        }
      }
    },
  });

  // Load user profile function
  const loadUserProfile = async () => {
    try {
      const profile = await apiClient.getDetailedProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  // Update editor editability based on read-only status
  useEffect(() => {
    if (editor && currentChapter) {
      // Disable editing if user is in read-only mode
      const shouldBeEditable = !userProfile?.is_read_only;
      editor.setEditable(shouldBeEditable);
    }
  }, [editor, userProfile?.is_read_only, currentChapter]);

  // Load memoir and chapters on mount - only when memoirId changes
  useEffect(() => {
    if (memoirId) {
      // Clear editor content when switching memoirs to prevent cross-memoir contamination
      if (editor) {
        editor.commands.setContent('<p>Please select a chapter</p>');
        editor.setEditable(false);
      }
      dispatch(fetchMemoir(memoirId));
      dispatch(fetchChapters(memoirId));
      dispatch(fetchTodos(memoirId));
    }

    // Load user profile for usage limits
    loadUserProfile();
  }, [dispatch, memoirId]);

  // Separate cleanup effect for timeouts
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // Load chapter content
  const loadChapterContent = useCallback(async (chapterIdToLoad: string, forceReload = false) => {
    console.log('📥 loadChapterContent called:', { chapterIdToLoad, forceReload, contentLoading, currentChapterId: currentChapter?.chapter_id });

    // Don't reload content if we're already loading this chapter, unless forced
    if (contentLoading) {
      console.log('⏳ Already loading content, skipping');
      return;
    }

    if (!forceReload && currentChapter?.chapter_id === chapterIdToLoad) {
      // Check if editor actually has content OR if it's been initialized with empty content
      const editorText = editor?.getText().trim() || '';
      const hasPlaceholder = editorText.includes('Please select a chapter') || editorText.includes('Loading');

      // Consider it loaded if:
      // 1. It has actual text content, OR
      // 2. It's empty but the editor is editable (meaning it's been successfully loaded as an empty chapter)
      const hasContent = (editorText && !hasPlaceholder) || (editor?.isEditable && !hasPlaceholder);

      if (hasContent) {
        console.log('✅ Content already loaded for this chapter, skipping');
        return;
      } else {
        console.log('⚠️ Chapter set but no content in editor, forcing reload');
        // Continue to load even though currentChapter matches
      }
    }

    try {
      setContentLoading(true);

      if (editor) {
        const proseMirrorElement = editor.view.dom;

        // Always clean up any existing classes first
        proseMirrorElement.classList.remove('loading', 'content-changing');
        editor.setEditable(false);

        // Force a small delay to ensure classes are removed
        await new Promise(resolve => setTimeout(resolve, 10));

        // Start fade-out - make content less visible
        proseMirrorElement.classList.add('loading');

        // Wait for fade-out to complete
        await new Promise(resolve => setTimeout(resolve, 250));

        // Load new content via API while editor is still faded
        const data = await apiClient.getChapterContent(chapterIdToLoad);

        // Double-check we're still on the same chapter (prevent race condition)
        const currentlySelectedChapter = window.location.pathname.split('/').pop();
        if (currentlySelectedChapter !== chapterIdToLoad) {
          proseMirrorElement.classList.remove('loading');
          return;
        }

        // Replace with actual content while still faded out
        if (data.content) {
          editor.commands.setContent(data.content);
        } else {
          editor.commands.setContent('<p></p>');
        }

        // Small delay to ensure content is set, then fade back in
        await new Promise(resolve => setTimeout(resolve, 50));

        // Remove loading class to fade content back in
        proseMirrorElement.classList.remove('loading');
        editor.setEditable(true);
      }
    } catch (error) {
      console.error('Failed to load chapter content:', error);
      if (editor) {
        const proseMirrorElement = editor.view.dom;
        proseMirrorElement.classList.remove('loading', 'content-changing');
        editor.commands.setContent('<p></p>');
        editor.setEditable(true);
      }
    } finally {
      setContentLoading(false);
    }
  }, [contentLoading, currentChapter?.chapter_id, editor]);

  // Correct chapter-memoir mismatch by navigating to the right chapter
  const correctChapterMemoirMismatch = useCallback(async () => {
    if (!memoir || !memoirId) {
      console.warn('Cannot correct mismatch: missing memoir data');
      return;
    }

    try {
      console.log('🔧 Correcting chapter-memoir mismatch...');

      // CRITICAL: Filter chapters to only include those belonging to current memoir
      const memoirChapters = chapters.filter(c => c.memoir_id === memoirId);

      if (memoirChapters.length === 0) {
        console.warn('❌ No chapters found for memoir during correction:', memoirId);
        return;
      }

      // Get the user's last viewed chapter for this memoir
      const preference = await apiClient.getLastViewedChapter(memoirId);

      let targetChapter = null;

      // If we have a saved preference, try to use it (from memoir-specific chapters)
      if (preference.last_viewed_chapter_id) {
        targetChapter = memoirChapters.find(c => c.chapter_id === preference.last_viewed_chapter_id);
        console.log('🎯 Found saved preference chapter:', targetChapter?.title);
      }

      // If no saved preference or saved chapter doesn't exist, use first chapter OF THIS MEMOIR
      if (!targetChapter && memoirChapters.length > 0) {
        targetChapter = memoirChapters[0];
        console.log('📝 Using first chapter of memoir as fallback:', targetChapter.title);
      }

      if (targetChapter) {
        console.log('🧭 Correcting navigation to:', targetChapter.title);
        setIsAutoNavigating(true);
        navigate(`/memoir/${memoirId}/chapter/${targetChapter.chapter_id}`, { replace: true });
        setTimeout(() => setIsAutoNavigating(false), 100);
      } else {
        console.warn('❌ No chapters available to navigate to');
      }
    } catch (error) {
      console.error('Failed to correct chapter-memoir mismatch:', error);
      // Fallback to first chapter OF THIS MEMOIR if API call fails
      const memoirChapters = chapters.filter(c => c.memoir_id === memoirId);
      if (memoirChapters.length > 0) {
        console.log('🔄 Fallback: navigating to first chapter of memoir');
        setIsAutoNavigating(true);
        navigate(`/memoir/${memoirId}/chapter/${memoirChapters[0].chapter_id}`, { replace: true });
        setTimeout(() => setIsAutoNavigating(false), 100);
      }
    }
  }, [memoir, memoirId, chapters, navigate]);

  // Simple auto-navigation: check user preference directly
  useEffect(() => {
    const handleAutoNavigation = async () => {
      // Only auto-navigate when:
      // 1. We have chapters loaded
      // 2. No chapter is selected in URL
      // 3. We have a memoir ID
      if (chapters.length > 0 && !chapterId && memoirId) {
        // CRITICAL: Filter chapters to only include those belonging to current memoir
        const memoirChapters = chapters.filter(c => c.memoir_id === memoirId);

        if (memoirChapters.length === 0) {
          console.warn('No chapters found for memoir:', memoirId);
          console.log('Available chapters:', chapters.map(c => ({ id: c.chapter_id, memoir: c.memoir_id })));
          return;
        }

        console.log(`📚 Found ${memoirChapters.length} chapters for memoir ${memoirId}`);

        try {
          // Get the user's last viewed chapter preference
          const preference = await apiClient.getLastViewedChapter(memoirId);

          let targetChapter = memoirChapters[0]; // Default to first chapter OF THIS MEMOIR

          // If we have a saved preference and that chapter exists in this memoir, use it
          if (preference.last_viewed_chapter_id) {
            const lastViewedChapter = memoirChapters.find(c => c.chapter_id === preference.last_viewed_chapter_id);
            if (lastViewedChapter) {
              targetChapter = lastViewedChapter;
              console.log('🎯 Using saved chapter for memoir:', targetChapter.title);
            } else {
              console.log('📝 Saved chapter not found in current memoir, using first chapter:', memoirChapters[0].title);
            }
          } else {
            console.log('📝 No saved preference, using first chapter:', memoirChapters[0].title);
          }

          setIsAutoNavigating(true);
          navigate(`/memoir/${memoirId}/chapter/${targetChapter.chapter_id}`, { replace: true });
          setTimeout(() => setIsAutoNavigating(false), 100);

        } catch (error) {
          console.error('Failed to get last viewed chapter, using first chapter:', error);
          // Fallback to first chapter OF THIS MEMOIR
          setIsAutoNavigating(true);
          navigate(`/memoir/${memoirId}/chapter/${memoirChapters[0].chapter_id}`, { replace: true });
          setTimeout(() => setIsAutoNavigating(false), 100);
        }
      }
    };

    handleAutoNavigation();
  }, [chapters, chapterId, memoirId, navigate]);

  // Load chapter content when chapter changes
  useEffect(() => {
    if (chapterId && chapters.length > 0) {
      const chapter = chapters.find(c => c.chapter_id === chapterId);

      // Check for chapter-memoir mismatch BEFORE loading content
      if (chapter && memoir && chapter.memoir_id !== memoir.memoir_id) {
        console.warn('🚨 Chapter-memoir mismatch detected in content loading!');
        console.log('Chapter memoir ID:', chapter.memoir_id);
        console.log('Current memoir ID:', memoir.memoir_id);
        console.log('Triggering correction...');
        correctChapterMemoirMismatch();
        return;
      }

      // Check if editor has actual content (not just the default placeholder)
      const editorText = editor?.getText().trim() || '';
      const editorHasContent = editor && editorText &&
        !editorText.includes('Please select a chapter') &&
        !editorText.includes('Loading');

      // More precise condition: load if:
      // 1. Chapter changed (most important - always reload on chapter change)
      // 2. No current chapter is set (initial load)
      // 3. Editor has no content and we're not already loading
      const shouldLoadContent = chapter && (
        currentChapter?.chapter_id !== chapterId ||  // Chapter changed - always load
        !currentChapter ||                            // No chapter set yet
        (!editorHasContent && !contentLoading)        // Editor empty and not loading
      );

      console.log('🔍 Content loading check:', {
        chapterId,
        hasCurrentChapter: !!currentChapter,
        currentChapterId: currentChapter?.chapter_id,
        chapterChanged: currentChapter?.chapter_id !== chapterId,
        editorHasContent,
        contentLoading,
        shouldLoadContent
      });

      if (shouldLoadContent) {
        // Cancel any pending auto-save to prevent race condition
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
          setAutoSaveTimeout(null);
        }

        // Update last viewed chapter in backend (for URL-based navigation)
        // Skip if this is an auto-navigation to avoid overwriting the saved preference
        if (memoirId && currentChapter?.chapter_id !== chapterId && !isAutoNavigating) {
          apiClient.updateLastViewedChapter(memoirId, chapterId).catch(error => {
            console.error('Failed to update last viewed chapter:', error);
          });
        }

        // Load chapter content - only force if chapter actually changed
        const forceReload = currentChapter?.chapter_id !== chapterId;
        loadChapterContent(chapterId, forceReload);

        // Set the current chapter after initiating content load
        dispatch(setCurrentChapter(chapter));
      }
    }
  }, [chapterId, chapters, dispatch, currentChapter?.chapter_id, loadChapterContent, autoSaveTimeout, memoirId, isAutoNavigating, editor, memoir, correctChapterMemoirMismatch]);

  // Fallback effect to handle stuck loading states
  useEffect(() => {
    // If we have chapters but no current chapter and we're not loading, something went wrong
    if (chapters.length > 0 && !currentChapter && !memoirLoading && !chapterLoading && !contentLoading && chapterId) {
      console.log('Fallback: Attempting to recover from stuck state');
      const chapter = chapters.find(c => c.chapter_id === chapterId);
      if (chapter) {
        dispatch(setCurrentChapter(chapter));
        if (editor) {
          loadChapterContent(chapterId, true);
        }
      }
    }
  }, [chapters, currentChapter, memoirLoading, chapterLoading, contentLoading, chapterId, dispatch, editor, loadChapterContent]);

  // Load notes when current chapter changes
  useEffect(() => {
    if (currentChapter?.chapter_id) {
      console.log('Loading notes for chapter:', currentChapter.chapter_id);
      dispatch(fetchNotes(currentChapter.chapter_id));
    }
  }, [currentChapter?.chapter_id, dispatch]);

  // Note management functions
  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentChapter || !newNoteContent.trim()) return;

    try {
      await dispatch(createNote({
        chapterId: currentChapter.chapter_id,
        data: {
          content: { text: newNoteContent.trim() } // Wrap in object as expected by API
        }
      }));
      setNewNoteContent('');
      setShowNewNoteModal(false);
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const handleEditNote = (noteId: string, content: Record<string, any>) => {
    setEditingNoteId(noteId);
    // Extract text from content object or use the whole content as string if it's already a string
    const textContent = content?.text || (typeof content === 'string' ? content : JSON.stringify(content));
    setEditingNoteContent(textContent);
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editingNoteContent.trim()) return;

    try {
      await dispatch(updateNote({
        noteId,
        data: {
          content: { text: editingNoteContent.trim() } // Wrap in object as expected by API
        }
      }));
      setEditingNoteId(null);
      setEditingNoteContent('');
    } catch (error: any) {
      console.error('Failed to update note:', error);
      alert('Note editing failed. Please check the console for details.');
      setEditingNoteId(null);
      setEditingNoteContent('');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await dispatch(deleteNote(noteId));
      } catch (error: any) {
        console.error('Failed to delete note:', error);
        alert('Note deletion failed. Please check the console for details.');
      }
    }
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  // Todo management functions
  const handleCreateTodo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memoirId || !newTodoDescription.trim()) return;

    try {
      await dispatch(createTodo({
        memoirId,
        data: {
          task_description: newTodoDescription.trim(),
          priority: newTodoPriority,
          due_date: newTodoDueDate ? `${newTodoDueDate}T00:00:00` : undefined,
          tags: []
        }
      }));
      setNewTodoDescription('');
      setNewTodoPriority('medium');
      setNewTodoDueDate('');
      setShowNewTodoModal(false);
    } catch (error) {
      console.error('Failed to create todo:', error);
    }
  };

  const handleToggleTodo = async (todoId: string, isCompleted: boolean) => {
    try {
      await dispatch(updateTodo({
        todoId,
        data: {
          is_completed: !isCompleted
        }
      }));
    } catch (error) {
      console.error('Failed to toggle todo:', error);
    }
  };

  const handleDeleteTodo = async (todoId: string) => {
    if (window.confirm('Are you sure you want to delete this todo?')) {
      try {
        await dispatch(deleteTodo(todoId));
      } catch (error) {
        console.error('Failed to delete todo:', error);
      }
    }
  };

  // Auto-save handler
  const handleAutoSave = useCallback(async (content: any, chapterIdAtTimeOfSave: string) => {
    // Safety checks to prevent saving to wrong chapter
    if (!currentChapter) {
      console.warn('No current chapter, skipping auto-save');
      return;
    }

    // Critical: Verify the chapter ID matches what was intended to be saved
    if (currentChapter.chapter_id !== chapterIdAtTimeOfSave) {
      console.warn(`Chapter changed during auto-save delay. Intended: ${chapterIdAtTimeOfSave}, Current: ${currentChapter.chapter_id}. Skipping save.`);
      return;
    }

    // Additional safety check: ensure we're still on the same memoir
    if (!memoir || currentChapter.memoir_id !== memoir.memoir_id) {
      console.warn('Chapter-memoir mismatch detected, attempting to correct...');
      console.log('Current chapter memoir ID:', currentChapter.memoir_id);
      console.log('Expected memoir ID:', memoir?.memoir_id || 'null');

      // Try to correct by navigating to the correct chapter for this memoir
      await correctChapterMemoirMismatch();
      return;
    }

    // Check URL matches current chapter to prevent race conditions
    const urlChapterId = window.location.pathname.split('/').pop();
    if (urlChapterId !== currentChapter.chapter_id) {
      console.warn(`URL chapter mismatch. URL: ${urlChapterId}, Current: ${currentChapter.chapter_id}. Skipping save.`);
      return;
    }

    try {
      console.log('Auto-saving content for chapter:', currentChapter.chapter_id);
      await dispatch(saveChapterContent({
        chapterId: currentChapter.chapter_id,
        data: { content }
      }));
    } catch (error) {
      console.error('Auto-save failed:', error);
      dispatch(setSaveStatus('error'));
    }
  }, [currentChapter, dispatch, memoir]);

  // Handle image upload
  const handleImageUpload = useCallback(async () => {
    if (!editor || userProfile?.is_read_only) return;

    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        // Create form data
        const formData = new FormData();
        formData.append('file', file);

        // Upload to backend
        const response = await apiClient.uploadImage(formData);

        // Insert image into editor using the signed download URL
        if (response.download_url) {
          editor.chain().focus().setImage({ src: response.download_url }).run();
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };

    input.click();
  }, [editor, userProfile?.is_read_only]);

  // AI Paragraph Improvement handlers
  const handleImproveParagraph = async (paragraphText: string, from: number, to: number) => {
    if (!currentChapter) {
      alert('Please select a chapter first.');
      return;
    }

    // Reset state and show modal
    setOriginalParagraphText(paragraphText);
    setImprovedParagraphText(null);
    setImprovementError(null);
    setImprovementUsageInfo(null);
    setShowAIImprovementModal(true);
    setIsImprovingParagraph(true);

    try {
      const result = await apiClient.improveParagraph(currentChapter.chapter_id, paragraphText);

      setImprovedParagraphText(result.improved_text);
      setImprovementUsageInfo(result.usage_info);

      // Store the position for applying the improvement
      setLastParagraphImprovement({
        originalText: paragraphText,
        improvedText: result.improved_text,
        position: { from, to }
      });

      // Reload user profile to update usage limits
      loadUserProfile();
    } catch (error: any) {
      console.error('Paragraph improvement failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to improve paragraph. Please try again.';
      setImprovementError(errorMessage);
    } finally {
      setIsImprovingParagraph(false);
    }
  };

  const handleAcceptImprovement = () => {
    if (!editor || !lastParagraphImprovement) return;

    const { improvedText, position } = lastParagraphImprovement;

    // Replace the paragraph text in the editor
    editor.chain()
      .focus()
      .setTextSelection({ from: position.from, to: position.to })
      .insertContent(improvedText)
      .run();

    // Close modal
    setShowAIImprovementModal(false);
  };

  const handleCancelImprovement = () => {
    setShowAIImprovementModal(false);
    setOriginalParagraphText('');
    setImprovedParagraphText(null);
    setImprovementError(null);
    setImprovementUsageInfo(null);
  };

  const handleUndoImprovement = () => {
    if (!editor || !lastParagraphImprovement) {
      alert('No recent improvement to undo.');
      return;
    }

    const { originalText, improvedText } = lastParagraphImprovement;

    // Find the improved text in the current document
    const currentText = editor.getText();
    const improvedIndex = currentText.indexOf(improvedText);

    if (improvedIndex === -1) {
      alert('Cannot find the improved text to undo. It may have been modified.');
      return;
    }

    // Calculate the position in the ProseMirror document
    // This is approximate - we find the text and replace it
    const tr = editor.state.tr;
    const doc = editor.state.doc;

    // Search through the document for the improved text
    let found = false;
    doc.descendants((node, pos) => {
      if (found) return false;

      if (node.isText && node.text?.includes(improvedText)) {
        const start = pos + (node.text.indexOf(improvedText));
        const end = start + improvedText.length;

        tr.replaceWith(start, end, editor.state.schema.text(originalText));
        found = true;
        return false;
      }
    });

    if (found) {
      editor.view.dispatch(tr);
      setLastParagraphImprovement(null);
      alert('Improvement undone successfully.');
    } else {
      alert('Cannot find the improved text to undo. It may have been modified.');
    }
  };

  // Initialize editable title when memoir changes
  useEffect(() => {
    if (memoir) {
      setEditableTitle(memoir.title);
    }
  }, [memoir]);

  // Handle title editing
  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditableTitle(memoir?.title || '');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setEditableTitle(newTitle);

    // Auto-save with debouncing
    if (titleSaveTimeout) {
      clearTimeout(titleSaveTimeout);
    }

    const timeout = setTimeout(() => {
      handleTitleSave(newTitle);
    }, 1000); // Save after 1 second of no typing

    setTitleSaveTimeout(timeout);
  };

  const handleTitleSave = async (title: string) => {
    if (!memoir || !title.trim() || title === memoir.title) return;

    try {
      await dispatch(updateMemoir({
        memoirId: memoir.memoir_id,
        data: { title: title.trim() }
      }));
      console.log('Memoir title updated successfully');
    } catch (error) {
      console.error('Failed to update memoir title:', error);
      // Revert to original title on error
      setEditableTitle(memoir.title);
    }
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (editableTitle.trim() !== memoir?.title) {
      handleTitleSave(editableTitle.trim());
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
      handleTitleSave(editableTitle.trim());
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      setEditableTitle(memoir?.title || '');
    }
  };

  // Chapter title editing handlers
  const handleChapterTitleClick = () => {
    if (!currentChapter) return;
    setIsEditingChapterTitle(true);
    setEditableChapterTitle(currentChapter.title);
  };

  const handleChapterTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setEditableChapterTitle(newTitle);

    // Auto-save with debouncing
    if (chapterTitleSaveTimeout) {
      clearTimeout(chapterTitleSaveTimeout);
    }

    const timeout = setTimeout(() => {
      handleChapterTitleSave(newTitle);
    }, 1000); // Save after 1 second of no typing

    setChapterTitleSaveTimeout(timeout);
  };

  const handleChapterTitleSave = async (title: string) => {
    if (!currentChapter || !title.trim() || title.trim() === currentChapter.title) {
      return;
    }

    try {
      await dispatch(updateChapter({
        chapterId: currentChapter.chapter_id,
        data: {
          title: title.trim(),
          memoir_id: currentChapter.memoir_id,
          section_group: currentChapter.section_group
        } as any
      }));
      console.log('Chapter title updated successfully');
    } catch (error) {
      console.error('Failed to update chapter title:', error);
      // Revert to original title on error
      setEditableChapterTitle(currentChapter.title);
    }
  };

  const handleChapterTitleBlur = () => {
    setIsEditingChapterTitle(false);
    if (currentChapter && editableChapterTitle.trim() !== currentChapter.title) {
      handleChapterTitleSave(editableChapterTitle.trim());
    }
  };

  const handleChapterTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingChapterTitle(false);
      handleChapterTitleSave(editableChapterTitle.trim());
    } else if (e.key === 'Escape') {
      setIsEditingChapterTitle(false);
      setEditableChapterTitle(currentChapter?.title || '');
    }
  };

  // Cleanup title save timeout
  useEffect(() => {
    return () => {
      if (titleSaveTimeout) {
        clearTimeout(titleSaveTimeout);
      }
    };
  }, [titleSaveTimeout]);

  // Cleanup chapter title save timeout
  useEffect(() => {
    return () => {
      if (chapterTitleSaveTimeout) {
        clearTimeout(chapterTitleSaveTimeout);
      }
    };
  }, [chapterTitleSaveTimeout]);

  // Handle group name editing
  const handleGroupNameClick = (groupName: string) => {
    if (groupName === 'Ungrouped') return; // Don't allow editing "Ungrouped"
    setEditingGroup(groupName);
    setEditableGroupName(groupName);
  };

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditableGroupName(newName);

    // Auto-save with debouncing
    if (groupSaveTimeout) {
      clearTimeout(groupSaveTimeout);
    }

    const timeout = setTimeout(() => {
      handleGroupNameSave(editingGroup!, newName);
    }, 1000); // Save after 1 second of no typing

    setGroupSaveTimeout(timeout);
  };

  const handleGroupNameSave = async (oldGroupName: string, newGroupName: string) => {
    if (!memoirId || !newGroupName.trim() || newGroupName === oldGroupName) return;

    try {
      // Get all chapters in this group
      const chaptersInGroup = chapters.filter(c => (c.section_group || 'Ungrouped') === oldGroupName);

      console.log(`Updating group name from "${oldGroupName}" to "${newGroupName.trim()}" for ${chaptersInGroup.length} chapters`);

      // Update each chapter's section_group
      const updatePromises = chaptersInGroup.map(chapter =>
        dispatch(updateChapter({
          chapterId: chapter.chapter_id,
          data: {
            title: chapter.title,
            memoir_id: chapter.memoir_id,
            section_group: newGroupName.trim()
          } as any
        }))
      );

      await Promise.all(updatePromises);
      console.log('Group name updated successfully');
    } catch (error) {
      console.error('Failed to update group name:', error);
      // Revert to original name on error
      setEditableGroupName(oldGroupName);
    }
  };

  const handleGroupNameBlur = () => {
    if (editingGroup && editableGroupName.trim() !== editingGroup) {
      handleGroupNameSave(editingGroup, editableGroupName.trim());
    }
    setEditingGroup(null);
  };

  const handleGroupNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingGroup) {
        handleGroupNameSave(editingGroup, editableGroupName.trim());
      }
      setEditingGroup(null);
    } else if (e.key === 'Escape') {
      setEditableGroupName(editingGroup || '');
      setEditingGroup(null);
    }
  };

  // Cleanup group save timeout
  useEffect(() => {
    return () => {
      if (groupSaveTimeout) {
        clearTimeout(groupSaveTimeout);
      }
    };
  }, [groupSaveTimeout]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#f59e0b';
      case 'review': return '#3b82f6';
      case 'complete': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#dc2626';
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const handleDragStart = (e: React.DragEvent, chapterId: string) => {
    setDraggedChapter(chapterId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', chapterId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleGroupDragOver = (e: React.DragEvent, groupName: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroup(groupName);
  };

  const handleGroupDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the group entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverGroup(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetChapterId: string) => {
    e.preventDefault();
    if (!draggedChapter || draggedChapter === targetChapterId || !memoirId) return;

    const draggedChapterObj = chapters.find(c => c.chapter_id === draggedChapter);
    const targetChapterObj = chapters.find(c => c.chapter_id === targetChapterId);

    if (!draggedChapterObj || !targetChapterObj) return;

    // Only allow reordering within the same group
    const draggedGroup = draggedChapterObj.section_group || 'Ungrouped';
    const targetGroup = targetChapterObj.section_group || 'Ungrouped';

    if (draggedGroup !== targetGroup) {
      // Different groups - don't reorder, let handleGroupDrop handle it
      setDraggedChapter(null);
      setDragOverGroup(null);
      return;
    }

    try {
      // Reorder chapters within the same group
      const draggedIdx = chapters.findIndex(c => c.chapter_id === draggedChapter);
      const targetIdx = chapters.findIndex(c => c.chapter_id === targetChapterId);

      const newChapters = [...chapters];
      const draggedItem = newChapters.splice(draggedIdx, 1)[0];
      newChapters.splice(targetIdx, 0, draggedItem);

      // Update the order in the backend
      const orderedChapterIds = newChapters.map(chapter => chapter.chapter_id);
      await dispatch(reorderChapters({ memoirId, chapterIds: orderedChapterIds }));

      console.log(`Reordered chapter "${draggedChapterObj.title}" within group "${draggedGroup}"`);
    } catch (error) {
      console.error('Failed to reorder chapters:', error);
    }

    setDraggedChapter(null);
    setDragOverGroup(null);
  };

  const handleGroupDrop = async (e: React.DragEvent, targetGroup: string) => {
    e.preventDefault();
    if (!draggedChapter || !memoirId) return;

    const draggedChapterObj = chapters.find(c => c.chapter_id === draggedChapter);
    if (!draggedChapterObj) return;

    // If dropping in the same group, do nothing
    const currentGroup = draggedChapterObj.section_group || 'Ungrouped';
    if (currentGroup === targetGroup) {
      setDraggedChapter(null);
      setDragOverGroup(null);
      return;
    }

    try {
      console.log(`Attempting to move chapter "${draggedChapterObj.title}" from "${currentGroup}" to "${targetGroup}"`);
      console.log('Current chapter data:', draggedChapterObj);

      // Prepare the update data with all required fields
      const updateData: any = {
        title: draggedChapterObj.title,
        memoir_id: draggedChapterObj.memoir_id
      };

      // Only include section_group if it's not 'Ungrouped'
      if (targetGroup !== 'Ungrouped') {
        updateData.section_group = targetGroup;
      } else {
        // For 'Ungrouped', explicitly set to null or undefined
        updateData.section_group = null;
      }

      console.log('Update data:', updateData);

      // Call API to update chapter group
      const result = await dispatch(updateChapter({
        chapterId: draggedChapter,
        data: updateData
      }));

      if (updateChapter.fulfilled.match(result)) {
        console.log(`Successfully moved chapter "${draggedChapterObj.title}" from "${currentGroup}" to "${targetGroup}"`);
        console.log('Updated chapter:', result.payload);
      } else {
        console.error('Update chapter action was rejected:', result);
        console.error('Error payload:', result.payload);
        console.error('Error details:', result.error);
      }

    } catch (error) {
      console.error('Failed to move chapter to new group:', error);
    }

    setDraggedChapter(null);
    setDragOverGroup(null);
  };

  const handleCreateChapter = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!memoirId) return;

    try {
      const result = await dispatch(createChapter({
        memoirId,
        data: {
          title: newChapterTitle,
          section_group: newChapterGroup || undefined
        }
      }));

      if (createChapter.fulfilled.match(result)) {
        setNewChapterTitle('');
        setNewChapterGroup('');
        setShowNewChapterModal(false);
        // Navigate to the new chapter
        navigate(`/memoir/${memoirId}/chapter/${result.payload.chapter_id}`);
      }
    } catch (error) {
      console.error('Failed to create chapter:', error);
    }
  };

  // Handle chapter selection
  const handleChapterSelect = async (chapter: any) => {
    console.log('Selecting chapter:', chapter.chapter_id);

    // CRITICAL: Cancel any pending auto-save immediately
    if (autoSaveTimeout) {
      console.log('Canceling pending auto-save for chapter selection');
      clearTimeout(autoSaveTimeout);
      setAutoSaveTimeout(null);
      dispatch(setSaveStatus('idle')); // Reset save status
    }

    // Update last viewed chapter in backend (this is a manual selection)
    if (memoirId) {
      try {
        console.log('DEBUG: Updating last viewed chapter via manual selection:', chapter.chapter_id);
        await apiClient.updateLastViewedChapter(memoirId, chapter.chapter_id);
        console.log('Updated last viewed chapter:', chapter.chapter_id);
      } catch (error) {
        console.error('Failed to update last viewed chapter:', error);
        // Don't block navigation if this fails
      }
    }

    // Always navigate to ensure the URL is correct
    navigate(`/memoir/${memoirId}/chapter/${chapter.chapter_id}`);

    // Also immediately set the current chapter to ensure state consistency
    dispatch(setCurrentChapter(chapter));

    // If the editor exists, load the chapter content
    if (editor) {
      editor.commands.setContent('<p>Loading...</p>');
      editor.setEditable(false); // Disable during loading
      loadChapterContent(chapter.chapter_id, true); // Force reload (this will re-enable editing)
    }
  };

  // Handle chapter deletion - show modal
  const handleDeleteChapterClick = () => {
    if (!currentChapter) return;
    setChapterToDelete(currentChapter);
    setShowDeleteChapterModal(true);
  };

  // Confirm chapter deletion
  const handleConfirmDeleteChapter = async () => {
    if (!chapterToDelete || !memoirId) return;

    try {
      await dispatch(deleteChapter(chapterToDelete.chapter_id));

      // Close modal
      setShowDeleteChapterModal(false);
      setChapterToDelete(null);

      // Navigate to first available chapter or memoir without chapter
      const remainingChapters = chapters.filter(c => c.chapter_id !== chapterToDelete.chapter_id);
      if (remainingChapters.length > 0) {
        navigate(`/memoir/${memoirId}/chapter/${remainingChapters[0].chapter_id}`);
      } else {
        navigate(`/memoir/${memoirId}`);
      }
    } catch (error) {
      console.error('Failed to delete chapter:', error);
      alert('Failed to delete chapter. Please try again.');
    }
  };

  // Cancel chapter deletion
  const handleCancelDeleteChapter = () => {
    setShowDeleteChapterModal(false);
    setChapterToDelete(null);
  };

  const groupedChapters = [...chapters]
    .sort((a, b) => a.order_index - b.order_index)
    .reduce((acc, chapter) => {
      const group = chapter.section_group || 'Ungrouped';
      if (!acc[group]) acc[group] = [];
      acc[group].push(chapter);
      return acc;
    }, {} as Record<string, any[]>);

  // Calculate total word count
  const totalWordCount = chapters.reduce((total, chapter) => total + (chapter.word_count || 0), 0);

  // Loading states
  if (memoirLoading || !memoir) {
    return (
      <div className="editor-layout">
        <div className="loading-container">
          <LoadingSpinner size="lg" text="Loading memoir..." />
        </div>
      </div>
    );
  }

  // Error state
  if (memoirError) {
    return (
      <div className="editor-layout">
        <div className="error-container">
          <div className="alert alert-error">
            {memoirError}
          </div>
          <Link to="/dashboard" className="btn btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`editor-layout ${isFocusMode ? 'focus-mode' : ''}`}>
      {/* Theme Toggle, Settings, and Focus Mode */}
      <div className="editor-theme-toggle">
        <Link to="/settings" className="settings-link" title="Settings">
          <SettingsIcon size={20} />
        </Link>
        {/* Mobile AI/Chapters toggle button */}
        <button
          className="mobile-panel-toggle"
          onClick={() => {
            if (mobileBottomPanel === 'chapters') {
              setMobileBottomPanel('ai');
              setMobileChaptersExpanded(false);
            } else {
              setMobileBottomPanel('chapters');
              setMobileAiExpanded(false);
            }
          }}
          title={mobileBottomPanel === 'chapters' ? 'Switch to AI Tools' : 'Switch to Chapters'}
        >
          {mobileBottomPanel === 'chapters' ? <Bot size={20} /> : <BookOpen size={20} />}
        </button>
        <ThemeToggle />
        <button
          className={`focus-mode-btn ${isFocusMode ? 'active' : ''} ${focusBtnHighlight ? 'highlight' : ''}`}
          onClick={() => setIsFocusMode(!isFocusMode)}
          title={isFocusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
        >
          <Focus size={20} />
        </button>
      </div>

      {/* Left Panel - Navigation */}
      <div className="left-panel">
        <div className="panel-header">
          <Link to="/dashboard" className="back-link">
            <ArrowLeft size={16} style={{ display: 'inline', marginRight: '4px' }} />
            Back to Dashboard
          </Link>
          {isEditingTitle && !userProfile?.is_read_only ? (
            <input
              type="text"
              value={editableTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className="memoir-title-input"
              placeholder="Enter memoir title..."
              autoFocus
            />
          ) : (
            <h2
              className={`memoir-title-display ${userProfile?.is_read_only ? '' : 'editable'}`}
              onClick={userProfile?.is_read_only ? undefined : handleTitleClick}
              title={userProfile?.is_read_only ? memoir.title : "Click to edit title"}
            >
              {memoir.title}
            </h2>
          )}
          <p className="memoir-stats">
            {chapters.length} chapter{chapters.length !== 1 ? 's' : ''} • {totalWordCount.toLocaleString()} words
          </p>
          <button
            className="btn btn-primary btn-export"
            onClick={() => setShowExportModal(true)}
            title="Export memoir to EPUB or PDF"
            style={{ marginTop: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            <Download size={18} />
            Export Book
          </button>
          <Link
            to={`/memoir/${memoirId}/settings/publishing`}
            className="btn btn-outline"
            title="Configure front and back covers for export"
            style={{ marginTop: '8px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none', boxSizing: 'border-box' }}
          >
            <BookOpen size={18} />
            Cover Image
          </Link>
        </div>

        <div className="chapters-section">
          <div className="section-header">
            <h3>Chapters</h3>
            {!userProfile?.is_read_only && (
              <button
                className="btn btn-sm btn-add-chapter"
                onClick={() => setShowNewChapterModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Plus size={16} />
                Add
              </button>
            )}
          </div>

          <div className="chapters-list">
            {chapterLoading ? (
              <div className="loading-container">
                <LoadingSpinner size="sm" text="Loading chapters..." />
              </div>
            ) : chapters.length === 0 ? (
              <div className="empty-state">
                <p>No chapters yet</p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowNewChapterModal(true)}
                >
                  Create First Chapter
                </button>
              </div>
            ) : (
              Object.entries(groupedChapters).map(([group, groupChapters]) => (
                <div
                  key={group}
                  className={`chapter-group ${dragOverGroup === group ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleGroupDragOver(e, group)}
                  onDragLeave={handleGroupDragLeave}
                  onDrop={(e) => handleGroupDrop(e, group)}
                >
                  <div className="group-header">
                    {editingGroup === group ? (
                      <input
                        type="text"
                        value={editableGroupName}
                        onChange={handleGroupNameChange}
                        onBlur={handleGroupNameBlur}
                        onKeyDown={handleGroupNameKeyDown}
                        className="group-title-input"
                        placeholder="Enter group name..."
                        autoFocus
                      />
                    ) : (
                      <span
                        className={`group-title ${group !== 'Ungrouped' ? 'editable' : ''}`}
                        onClick={() => handleGroupNameClick(group)}
                        title={group !== 'Ungrouped' ? 'Click to edit group name' : ''}
                      >
                        {group}
                      </span>
                    )}
                    <span className="group-count">({groupChapters.length})</span>
                    {draggedChapter && dragOverGroup === group && (
                      <span className="drop-indicator">Drop here to move to this group</span>
                    )}
                  </div>
                  {groupChapters.map(chapter => (
                    <div
                      key={chapter.chapter_id}
                      className={`chapter-item ${currentChapter?.chapter_id === chapter.chapter_id ? 'active' : ''} ${draggedChapter === chapter.chapter_id ? 'dragging' : ''
                        }`}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, chapter.chapter_id)}
                    >
                      <div
                        className="drag-handle"
                        draggable
                        onDragStart={(e) => handleDragStart(e, chapter.chapter_id)}
                      >
                        <GripVertical size={16} />
                      </div>
                      <div
                        className="chapter-info"
                        onClick={() => handleChapterSelect(chapter)}
                      >
                        <h4>{chapter.title}</h4>
                        <div className="chapter-meta">
                          <span className="word-count">{chapter.word_count || 0} words</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Empty drop zone when group has no chapters */}
                  {groupChapters.length === 0 && draggedChapter && (
                    <div className="empty-group-drop-zone">
                      Drop chapter here
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Section - Chapters or AI Tools */}
      <div className="mobile-bottom-section">
        {mobileBottomPanel === 'chapters' ? (
          /* Mobile Chapters Panel */
          <>
            <div className="mobile-chapters-header" onClick={() => setMobileChaptersExpanded(!mobileChaptersExpanded)}>
              <div className="mobile-chapters-title">
                <h3>Chapters</h3>
                <span className="chapter-count">({chapters.length})</span>
              </div>
              <div className="mobile-chapters-actions">
                {!userProfile?.is_read_only && (
                  <button
                    className="btn btn-sm btn-add-chapter"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNewChapterModal(true);
                    }}
                  >
                    <Plus size={16} />
                    Add
                  </button>
                )}
                <button className="expand-toggle">
                  {mobileChaptersExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
            </div>

            {/* Current chapter indicator when collapsed */}
            {!mobileChaptersExpanded && currentChapter && (
              <div className="current-chapter-indicator">
                <span className="current-label">Current:</span>
                <span className="current-chapter-name">{currentChapter.title}</span>
              </div>
            )}

            {/* Expanded chapter list */}
            {mobileChaptersExpanded && (
              <div className="mobile-chapters-list">
                {chapters.length === 0 ? (
                  <div className="empty-state">
                    <p>No chapters yet</p>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowNewChapterModal(true)}
                    >
                      Create First Chapter
                    </button>
                  </div>
                ) : (
                  Object.entries(groupedChapters).map(([group, groupChapters]) => (
                    <div key={group} className="mobile-chapter-group">
                      <div className="mobile-group-header">
                        <span className="mobile-group-title">{group}</span>
                        <span className="mobile-group-count">({groupChapters.length})</span>
                      </div>
                      {groupChapters.map(chapter => (
                        <div
                          key={chapter.chapter_id}
                          className={`mobile-chapter-item ${currentChapter?.chapter_id === chapter.chapter_id ? 'active' : ''}`}
                          onClick={() => {
                            handleChapterSelect(chapter);
                            setMobileChaptersExpanded(false);
                          }}
                        >
                          <h4>{chapter.title}</h4>
                          <span className="word-count">{chapter.word_count || 0} words</span>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        ) : (
          /* Mobile AI Tools Panel */
          <>
            <div className="mobile-ai-header" onClick={() => setMobileAiExpanded(!mobileAiExpanded)}>
              <div className="mobile-ai-title">
                <h3>AI Tools</h3>
              </div>
              <div className="mobile-ai-actions">
                <button className="expand-toggle">
                  {mobileAiExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
            </div>

            {/* Collapsed AI indicator */}
            {!mobileAiExpanded && (
              <div className="mobile-ai-indicator">
                <span className="ai-hint">Tap to expand AI tools</span>
              </div>
            )}

            {/* Expanded AI Tools */}
            {mobileAiExpanded && (
              <div className="mobile-ai-content">
                <div className="ai-sub-tabs">
                  <button
                    className={`ai-sub-tab ${aiSubTab === 'chat' ? 'active' : ''}`}
                    onClick={() => setAiSubTab('chat')}
                  >
                    Chat
                  </button>
                  <button
                    className={`ai-sub-tab ${aiSubTab === 'image' ? 'active' : ''}`}
                    onClick={() => setAiSubTab('image')}
                  >
                    Image
                  </button>
                  <button
                    className={`ai-sub-tab ${aiSubTab === 'analysis' ? 'active' : ''}`}
                    onClick={() => setAiSubTab('analysis')}
                  >
                    Suggestions
                  </button>
                </div>

                <div className="mobile-ai-sub-content">
                  {aiSubTab === 'analysis' && (
                    <>
                      {currentChapter ? (
                        <ChapterAnalysis
                          chapterId={currentChapter.chapter_id}
                          chapterTitle={currentChapter.title}
                          chapterContent={editor?.getText() || ''}
                          onUsageUpdate={() => {
                            if (userProfile) {
                              loadUserProfile();
                            }
                          }}
                        />
                      ) : (
                        <div className="ai-tab-empty">
                          <div className="empty-state">
                            <span className="empty-icon"><BarChart3 size={32} strokeWidth={1.5} /></span>
                            <p>Select a chapter to analyze</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {aiSubTab === 'chat' && memoirId && userProfile && (
                    <ChatInterface
                      memoirId={memoirId}
                      userProfile={userProfile}
                      isVisible={mobileAiExpanded && aiSubTab === 'chat'}
                    />
                  )}

                  {aiSubTab === 'image' && (
                    <>
                      {currentChapter && editor && userProfile ? (
                        <ImageGeneration
                          chapterId={currentChapter.chapter_id}
                          chapterTitle={currentChapter.title}
                          userProfile={userProfile}
                          onImageGenerated={(imageUrl) => {
                            editor.chain().focus().setImage({ src: imageUrl }).run();
                          }}
                          onUsageUpdate={() => {
                            if (userProfile) {
                              loadUserProfile();
                            }
                          }}
                        />
                      ) : (
                        <div className="ai-tab-empty">
                          <div className="empty-state">
                            <span className="empty-icon"><ImageIcon size={32} strokeWidth={1.5} /></span>
                            <p>Select a chapter to generate images</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Center Panel - Editor */}
      <div className="center-panel">
        {/* Read-only banner for canceled subscriptions */}
        {userProfile?.is_read_only && (
          <div className="read-only-banner">
            <span className="banner-icon">🔒</span>
            <div className="banner-content">
              <strong>Read-Only Mode</strong>
              <p>Your subscription has been canceled. You can view your memoirs but editing is disabled.</p>
              <a href="/subscription" className="banner-link">Reactivate Subscription</a>
            </div>
          </div>
        )}

        {currentChapter && (
          <div className="editor-header">
            <div className="editor-status">
              <span className={`save-status ${saveStatus}`}>
                {saveStatus === 'saving' && '💾 Saving...'}
                {saveStatus === 'saved' && lastSaved && `✓ Saved ${new Date(lastSaved).toLocaleTimeString()}`}
                {saveStatus === 'error' && '❌ Save failed'}
                {saveStatus === 'idle' && ''}
              </span>
              <span className="word-count">
                {currentChapter?.word_count?.toLocaleString() || 0} words
              </span>
            </div>
          </div>
        )}

        <div className="editor-container">
          {contentLoading ? (
            <div className="editor-loading">
              <LoadingSpinner size="md" text="Loading chapter content..." />
            </div>
          ) : chapters.length === 0 ? (
            <div className="editor-empty">
              <div className="empty-state-content">
                <h3>No chapters yet</h3>
                <p>Create your first chapter to start writing your memoir.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowNewChapterModal(true)}
                >
                  Create First Chapter
                </button>
              </div>
            </div>
          ) : !currentChapter ? (
            <div className="editor-empty">
              <div className="empty-state-content">
                <h3>Select a chapter to edit</h3>
                <p>Choose a chapter from the left panel to start writing.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Rich Text Editor Toolbar */}
              <EditorToolbar
                editor={editor}
                isReadOnly={userProfile?.is_read_only}
                onImageUpload={handleImageUpload}
                onUndoImprovement={handleUndoImprovement}
                hasImprovementToUndo={!!lastParagraphImprovement}
              />

              <div className="editor-content">
                {/* Chapter header matching PDF export layout - editable */}
                {currentChapter && (
                  <div className="editor-chapter-header">
                    <span className="editor-chapter-number">
                      Chapter {chapters.findIndex(c => c.chapter_id === currentChapter.chapter_id) + 1}
                    </span>
                    {isEditingChapterTitle && !userProfile?.is_read_only ? (
                      <input
                        type="text"
                        value={editableChapterTitle}
                        onChange={handleChapterTitleChange}
                        onBlur={handleChapterTitleBlur}
                        onKeyDown={handleChapterTitleKeyDown}
                        className="editor-chapter-title-input"
                        placeholder="Enter chapter title..."
                        autoFocus
                      />
                    ) : (
                      <div className="editor-chapter-title-wrapper">
                        <h1
                          className={`editor-chapter-title ${userProfile?.is_read_only ? '' : 'editable'}`}
                          onClick={userProfile?.is_read_only ? undefined : handleChapterTitleClick}
                          title={userProfile?.is_read_only ? currentChapter?.title : "Click to edit chapter title"}
                        >
                          {currentChapter.title}
                        </h1>
                        {!userProfile?.is_read_only && (
                          <button
                            className="delete-chapter-btn"
                            onClick={handleDeleteChapterClick}
                            title="Delete chapter"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <EditorContent editor={editor} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - Tools */}
      <div className="right-panel">


        <div className="panel-tabs">
          <button
            className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            AI Tools
          </button>
          <button
            className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </button>
          <button
            className={`tab ${activeTab === 'todos' ? 'active' : ''}`}
            onClick={() => setActiveTab('todos')}
          >
            To-Do
          </button>
        </div>

        <div className="panel-content">
          {activeTab === 'notes' && (
            <div className="notes-tab">
              <div className="tab-header">
                <h3>Notes</h3>
                {!userProfile?.is_read_only && (
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowNewNoteModal(true)}
                    disabled={!currentChapter}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={16} />
                    Add
                  </button>
                )}
              </div>
              <div className="notes-list">
                {notes.length === 0 ? (
                  <div className="empty-state">
                    <p>No notes yet. Add your first note to get started!</p>
                  </div>
                ) : (
                  notes.map(note => (
                    <div key={note.note_id} className={`note-item ${note.is_pinned ? 'pinned' : ''}`}>
                      {note.is_pinned && <div className="pin-indicator"><Pin size={14} fill="currentColor" /></div>}

                      {editingNoteId === note.note_id ? (
                        <div className="note-edit-form">
                          <textarea
                            value={editingNoteContent}
                            onChange={(e) => setEditingNoteContent(e.target.value)}
                            className="note-edit-input"
                            rows={3}
                            autoFocus
                          />
                          <div className="note-edit-actions">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleUpdateNote(note.note_id)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelNoteEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="note-content">
                            <p>{note.content?.text || (typeof note.content === 'string' ? note.content : JSON.stringify(note.content))}</p>
                          </div>
                          <div className="note-footer">
                            <span className="note-date">{new Date(note.created_at).toLocaleDateString()}</span>
                            {!userProfile?.is_read_only && (
                              <div className="note-actions">
                                <button
                                  className="note-action-btn"
                                  onClick={() => handleEditNote(note.note_id, note.content)}
                                  title="Edit note"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  className="note-action-btn delete"
                                  onClick={() => handleDeleteNote(note.note_id)}
                                  title="Delete note"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'todos' && (
            <div className="todos-tab">
              <div className="tab-header">
                <h3>To-Do List</h3>
                {!userProfile?.is_read_only && (
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowNewTodoModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={16} />
                    Add
                  </button>
                )}
              </div>
              <div className="todos-list">
                {todos.map(todo => (
                  <div key={todo.todo_id} className={`todo-item ${todo.is_completed ? 'completed' : ''}`}>
                    <input
                      type="checkbox"
                      checked={todo.is_completed}
                      onChange={() => handleToggleTodo(todo.todo_id, todo.is_completed)}
                      disabled={userProfile?.is_read_only}
                    />
                    <div className="todo-content">
                      <p>{todo.task_description}</p>
                      <div className="todo-meta">
                        <span
                          className="priority"
                          style={{ color: getPriorityColor(todo.priority) }}
                        >
                          {todo.priority}
                        </span>
                        {todo.due_date && <span className="due-date">{new Date(todo.due_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    {!userProfile?.is_read_only && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteTodo(todo.todo_id)}
                        title="Delete todo"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="ai-tab">
              <div className="ai-sub-tabs">
                <button
                  className={`ai-sub-tab ${aiSubTab === 'chat' ? 'active' : ''}`}
                  onClick={() => setAiSubTab('chat')}
                >
                  Chat
                </button>
                <button
                  className={`ai-sub-tab ${aiSubTab === 'image' ? 'active' : ''}`}
                  onClick={() => setAiSubTab('image')}
                >
                  Image
                </button>
                <button
                  className={`ai-sub-tab ${aiSubTab === 'analysis' ? 'active' : ''}`}
                  onClick={() => setAiSubTab('analysis')}
                >
                  Suggestions
                </button>
              </div>

              <div className="ai-sub-content">
                {aiSubTab === 'analysis' && (
                  <>
                    {currentChapter ? (
                      <ChapterAnalysis
                        chapterId={currentChapter.chapter_id}
                        chapterTitle={currentChapter.title}
                        chapterContent={editor?.getText() || ''}
                        onUsageUpdate={() => {
                          if (userProfile) {
                            loadUserProfile();
                          }
                        }}
                      />
                    ) : (
                      <div className="ai-tab-empty">
                        <div className="empty-state">
                          <span className="empty-icon"><BarChart3 size={48} strokeWidth={1.5} /></span>
                          <h4>AI Chapter Analysis</h4>
                          <p>Select a chapter to analyze its content and get writing insights.</p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {aiSubTab === 'chat' && memoirId && userProfile && (
                  <ChatInterface
                    memoirId={memoirId}
                    userProfile={userProfile}
                    isVisible={activeTab === 'ai' && aiSubTab === 'chat'}
                  />
                )}

                {aiSubTab === 'image' && (
                  <>
                    {currentChapter && editor && userProfile ? (
                      <ImageGeneration
                        chapterId={currentChapter.chapter_id}
                        chapterTitle={currentChapter.title}
                        userProfile={userProfile}
                        onImageGenerated={(imageUrl) => {
                          editor.chain().focus().setImage({ src: imageUrl }).run();
                        }}
                        onUsageUpdate={() => {
                          if (userProfile) {
                            loadUserProfile();
                          }
                        }}
                      />
                    ) : (
                      <div className="ai-tab-empty">
                        <div className="empty-state">
                          <span className="empty-icon"><ImageIcon size={48} strokeWidth={1.5} /></span>
                          <h4>AI Image Generation</h4>
                          <p>Select a chapter to generate AI images based on your content.</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* AI Usage Limits - Bottom of Right Panel */}
        {userProfile && (
          <div className="right-panel-footer">
            <UsageLimits userProfile={userProfile} compact={true} />
          </div>
        )}
      </div>

      {/* New Chapter Modal */}
      {
        showNewChapterModal && (
          <div className="modal-overlay" onClick={() => setShowNewChapterModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Chapter</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowNewChapterModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateChapter} className="modal-form">
                <div className="form-group">
                  <label htmlFor="chapterTitle">Chapter Title</label>
                  <input
                    type="text"
                    id="chapterTitle"
                    value={newChapterTitle}
                    onChange={(e) => setNewChapterTitle(e.target.value)}
                    placeholder="Enter chapter title..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="chapterGroup">Section Group (Optional)</label>
                  <input
                    type="text"
                    id="chapterGroup"
                    value={newChapterGroup}
                    onChange={(e) => setNewChapterGroup(e.target.value)}
                    placeholder="e.g., Childhood, Career, Family..."
                  />
                  <small>Leave empty to add to 'Ungrouped' section</small>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowNewChapterModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Chapter
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* New Note Modal */}
      {
        showNewNoteModal && (
          <div className="modal-overlay" onClick={() => setShowNewNoteModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Note</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowNewNoteModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateNote} className="modal-form">
                <div className="form-group">
                  <label htmlFor="noteContent">Note Content</label>
                  <textarea
                    id="noteContent"
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    placeholder="Enter your note..."
                    rows={4}
                    required
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowNewNoteModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Note
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* New Todo Modal */}
      {
        showNewTodoModal && (
          <div className="modal-overlay" onClick={() => setShowNewTodoModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Todo</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowNewTodoModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateTodo} className="modal-form">
                <div className="form-group">
                  <label htmlFor="todoDescription">Task Description</label>
                  <textarea
                    id="todoDescription"
                    value={newTodoDescription}
                    onChange={(e) => setNewTodoDescription(e.target.value)}
                    placeholder="Describe the task..."
                    rows={3}
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="todoPriority">Priority</label>
                  <select
                    id="todoPriority"
                    value={newTodoPriority}
                    onChange={(e) => setNewTodoPriority(e.target.value as 'low' | 'medium' | 'high' | 'urgent')}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="todoDueDate">Due Date (Optional)</label>
                  <input
                    type="date"
                    id="todoDueDate"
                    value={newTodoDueDate}
                    onChange={(e) => setNewTodoDueDate(e.target.value)}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowNewTodoModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Todo
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Delete Chapter Confirmation Modal */}
      {
        showDeleteChapterModal && chapterToDelete && (
          <div className="modal-overlay" onClick={handleCancelDeleteChapter}>
            <div className="modal modal-delete" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Delete Chapter</h2>
                <button
                  className="close-btn"
                  onClick={handleCancelDeleteChapter}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="modal-content">
                <p className="delete-warning">
                  Are you sure you want to delete the chapter titled "{chapterToDelete.title}"?
                </p>
                <p className="delete-warning-note">
                  <strong>Warning:</strong> This action is irreversible. All content, notes, and tasks within this chapter will be permanently removed.
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteChapter}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteChapter}
                >
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* AI Paragraph Improvement Modal */}
      <AIImprovementModal
        isOpen={showAIImprovementModal}
        isLoading={isImprovingParagraph}
        originalText={originalParagraphText}
        improvedText={improvedParagraphText}
        error={improvementError}
        usageInfo={improvementUsageInfo}
        onAccept={handleAcceptImprovement}
        onCancel={handleCancelImprovement}
      />

      {/* Export Modal */}
      {
        memoir && (
          <ExportModal
            open={showExportModal}
            onClose={() => setShowExportModal(false)}
            memoirId={memoir.memoir_id}
            memoirTitle={memoir.title}
          />
        )
      }
    </div >
  );
};

export default Editor;